// Firebase Authentication Service (Email/Password)
import { User } from '../types';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
  onAuthStateChanged,
  ActionCodeSettings,
  User as FirebaseUser,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export interface AuthUser extends User {
  firebaseUser?: FirebaseUser;
}

// Utility function to convert Firebase error codes to user-friendly messages
export const getFirebaseErrorMessage = (error: any): string => {
  const errorCode = error?.code || '';
  const errorMessage = error?.message || '';

  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please try signing in instead.';
    case 'auth/weak-password':
      return 'Password is too weak. Please choose a stronger password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled.';
    default:
      // If it's already a user-friendly message, return it
      if (errorMessage && !errorMessage.includes('Firebase:')) {
        return errorMessage;
      }
      // Otherwise return a generic message
      return 'An error occurred. Please try again.';
  }
};

// ActionCodeSettings for email link authentication
const actionCodeSettings: ActionCodeSettings = {
  url: process.env.NODE_ENV === 'production'
    ? 'https://afya-tracker-app.web.app/finishSignUp' // Replace with your production domain
    : 'http://localhost:3000/finishSignUp',
  handleCodeInApp: true,
};

// Send sign-in link to email
export const sendSignInLinkToEmailAddress = async (email: string): Promise<void> => {
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }

  try {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save email to localStorage for later use
    window.localStorage.setItem('emailForSignIn', email);
  } catch (error) {
    console.error('Error sending sign-in link:', error);
    throw error;
  }
};

// Check if the current URL is a sign-in with email link
export const isSignInWithEmailLinkUrl = (url: string): boolean => {
  if (!auth) return false;
  return isSignInWithEmailLink(auth, url);
};

// Complete sign-in with email link
export const signInWithEmailLinkUrl = async (email: string, emailLink: string): Promise<AuthUser> => {
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }

  try {
    const result = await signInWithEmailLink(auth, email, emailLink);
    const firebaseUser = result.user;

    // Clear email from localStorage
    window.localStorage.removeItem('emailForSignIn');

    // Check if there's pending signup data
    const pendingSignupData = window.localStorage.getItem('pendingSignupData');
    let userRole: 'patient' | 'provider' | 'admin' = 'patient'; // Default role

    if (pendingSignupData) {
      try {
        const signupData = JSON.parse(pendingSignupData);
        const role = signupData.role;
        if (role === 'patient' || role === 'provider' || role === 'admin') {
          userRole = role;
        }

        // Here you would typically save the user profile data to Firestore
        // For now, we'll just use the data to set the role
        console.log('Signup data found:', signupData);

        // Clear the pending signup data
        window.localStorage.removeItem('pendingSignupData');
      } catch (parseError) {
        console.error('Error parsing pending signup data:', parseError);
      }
    }

    // Create AuthUser object
    const authUser: AuthUser = {
      id: firebaseUser.uid,
      name: firebaseUser.displayName || firebaseUser.email || '',
      email: firebaseUser.email || '',
      role: userRole,
      firebaseUser
    };

    return authUser;
  } catch (error) {
    console.error('Error signing in with email link:', error);
    throw error;
  }
};

// Logout user
export const logoutUser = async (): Promise<void> => {
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }

  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Refresh user data from Firestore
export const refreshUserData = async (firebaseUser: any): Promise<AuthUser> => {
  // Try to get user data from Firestore
  let userRole: 'patient' | 'provider' | 'admin' | 'institution' = 'patient'; // Default
  let userStatus: 'active' | 'pending_approval' | 'suspended' | 'rejected' | undefined;

  try {
    if (db) {
      const docRef = doc(db, 'users', firebaseUser.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const userData = docSnap.data();
        const role = userData?.role;
        const status = userData?.status;

        if (role === 'patient' || role === 'provider' || role === 'admin' || role === 'institution') {
          userRole = role;
        }
        if (status === 'active' || status === 'pending_approval' || status === 'suspended' || status === 'rejected') {
          userStatus = status;
        }
      }
    }
  } catch (firestoreError) {
    console.warn('Could not fetch user data from Firestore, using defaults:', firestoreError);
    // Continue with default values
  }

  const authUser: AuthUser = {
    id: firebaseUser.uid,
    name: firebaseUser.displayName || firebaseUser.email || '',
    email: firebaseUser.email || '',
    role: userRole,
    status: userStatus,
    firebaseUser
  };

  return authUser;
};

// Listen for authentication state changes
export const onAuthStateChange = (
  callback: (user: AuthUser | null) => void
): (() => void) => {
  if (!auth) {
    callback(null);
    return () => {};
  }

  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const authUser = await refreshUserData(firebaseUser);
      callback(authUser);
    } else {
      callback(null);
    }
  });
};

// Email/Password Authentication Functions
export const loginWithEmailAndPassword = async (email: string, password: string): Promise<AuthUser> => {
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }

  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = result.user;

    // Try to get user data from Firestore
    let userRole: 'patient' | 'provider' | 'admin' | 'institution' = 'patient'; // Default
    let userStatus: 'active' | 'pending_approval' | 'suspended' | 'rejected' | undefined;

    try {
      if (db) {
        const docRef = doc(db, 'users', firebaseUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const role = userData?.role;
          const status = userData?.status;
          if (role === 'patient' || role === 'provider' || role === 'admin' || role === 'institution') {
            userRole = role;
          }
          if (status === 'active' || status === 'pending_approval' || status === 'suspended' || status === 'rejected') {
            userStatus = status;
          }
        }
      }
    } catch (firestoreError) {
      console.warn('Could not fetch user data from Firestore, using defaults:', firestoreError);
      // Continue with default values
    }

    // Create AuthUser object
    const authUser: AuthUser = {
      id: firebaseUser.uid,
      name: firebaseUser.displayName || firebaseUser.email || '',
      email: firebaseUser.email || '',
      role: userRole,
      status: userStatus,
      firebaseUser
    };

    return authUser;
  } catch (error) {
    console.error('Error signing in with email/password:', error);
    throw error;
  }
};

export const signupWithEmailAndPassword = async (
  email: string,
  password: string,
  userData: Partial<User> & { role: 'patient' | 'provider' | 'admin' } & Record<string, any>
): Promise<AuthUser> => {
  if (!auth) {
    throw new Error('Firebase auth not initialized');
  }

  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = result.user;

    // Update the user's display name if provided
    if (userData.name) {
      await updateProfile(firebaseUser, {
        displayName: userData.name
      });
    }

    // Save user data to Firestore
    if (db) {
      try {
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        await setDoc(userDocRef, {
          ...userData,
          id: firebaseUser.uid,
          email: firebaseUser.email,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        console.log('User data saved to Firestore:', userData);
      } catch (firestoreError) {
        console.error('Error saving user data to Firestore:', firestoreError);
        // Continue with signup even if Firestore save fails
      }
    } else {
      console.warn('Firestore not initialized, user data not saved');
    }

    // Create AuthUser object
    const authUser: AuthUser = {
      id: firebaseUser.uid,
      name: userData.name || firebaseUser.displayName || firebaseUser.email || '',
      email: firebaseUser.email || '',
      role: userData.role || 'patient',
      firebaseUser
    };

    return authUser;
  } catch (error) {
    console.error('Error signing up with email/password:', error);
    throw error;
  }
};